// src/portals/parent/tabs/mobile/ActionsTab.tsx
import React, { useState } from "react";
import type { ActionDetail, ActionsTabProps } from "../../actions/types";
import {
  countText,
  formatRange,
  formatTimeRange,
  getChildAge,
  getClassLabel,
  getDisplayName,
  getRequestSubtitle,
  getRequestTitle,
} from "../../actions/utils/actionUtils";
import ActionDetailView from "../../actions/components/ActionDetailView";
import CompactProcessingItem from "../../actions/components/CompactProcessingItem";
import RequestDetailView from "../../actions/components/RequestDetailView";
import SummaryStat from "../../actions/components/SummaryStat";
import TaskCard from "../../actions/components/TaskCard";
import {
  AlertIcon,
  CheckIcon,
  ClockIcon,
  FileTextIcon,
  HelpIcon,
  HomeIcon,
  InboxIcon,
  InfoIcon,
  UserIcon,
  UsersIcon,
} from "../../actions/components/icons";
import { getMutationRequestChildName } from "../../utils/mutationUtils";

type MobileActionsTabProps = ActionsTabProps & {
  childrenList?: any[];
  families?: any[];
  parentData?: any;
};

const ActionsTab = ({
  t,
  missingChildren = [],
  onFixChild = () => {},
  childrenList = [],
  families = [],
  parentData = null,
  alumniChildren = [],
  onAlumniAction = () => {},
  outingActions = [],
  onOpenOuting = () => {},
  privacyActions = [],
  onOpenPrivacyAction = () => {},
  healthActions = [],
  onOpenHealthAction = () => {},
  requests = [],
  parentMeetingInvitations = [],
  onSelectRequest = () => {},
  tripFormDraft = null,
  onTripFormChange = () => {},
  onTripFormSubmit = () => {},
  tripFormSaving = false,
  tripFormError = null,
  currentParentId = null,
  onCloseTripRequest = () => {},
  onOpenParentMeetingInvitation = () => {},
}: MobileActionsTabProps) => {
  const [selectedAction, setSelectedAction] = useState<ActionDetail | null>(
    null,
  );
  const [selectedRequest, setSelectedRequest] = useState<any | null>(null);

  const pendingRequests = Array.isArray(requests)
    ? requests.filter(
        (request) => String(request?.status || "").toUpperCase() === "PENDING",
      )
    : [];
  const isBankAccountRequest = (request: any) =>
    String(request?.entityType || "").toUpperCase() === "BANK_ACCOUNT";
  const onlyBankAccountPendingRequests =
    pendingRequests.length > 0 && pendingRequests.every(isBankAccountRequest);

  const openMeetingInvitations = Array.isArray(parentMeetingInvitations)
    ? parentMeetingInvitations.filter((invitation) =>
        ["INVITED", "RESERVED"].includes(
          String(invitation?.status || "").toUpperCase(),
        ),
      )
    : [];

  const urgentCount = missingChildren.length;
  const waitingCount =
    alumniChildren.length +
    outingActions.length +
    privacyActions.length +
    healthActions.length +
    openMeetingInvitations.length;
  const processingCount = pendingRequests.length;
  const totalCount = urgentCount + waitingCount + processingCount;

  const totalSubtitle =
    totalCount > 0
      ? countText(
          totalCount,
          t("parentPortal.actions.one_open_action", {
            defaultValue: "1 open action",
          }),
          t("parentPortal.actions.open_actions_count", {
            defaultValue: "{{count}} open actions",
            count: totalCount,
          }),
        )
      : t("parentPortal.actions.none_description", {
          defaultValue: "Everything is up to date.",
        });

  const hasOpenCards = urgentCount + waitingCount > 0;


  const openActionDetail = (action: ActionDetail) => {
    setSelectedRequest(null);
    setSelectedAction(action);
  };
  const backToOverview = () => setSelectedAction(null);
  if (selectedRequest) {
    return (
      <RequestDetailView
        t={t}
        request={selectedRequest}
        childrenList={childrenList}
        families={families}
        parentData={parentData}
        onBack={() => setSelectedRequest(null)}
        onSelectRequest={onSelectRequest}
      />
    );
  }

  if (selectedAction) {
    return (
      <ActionDetailView
        t={t}
        action={selectedAction}
        onBack={backToOverview}
        tripFormDraft={tripFormDraft}
        onTripFormChange={onTripFormChange}
        onTripFormSubmit={onTripFormSubmit}
        onCloseTripRequest={onCloseTripRequest}
        tripFormSaving={tripFormSaving}
        tripFormError={tripFormError}
      />
    );
  }

  return (
    <div className="pp-actions-page pp-actions-app-view">
      <section className="pp-actions-hero">
        <div className="pp-actions-hero-head">
          <div className="pp-actions-hero-copy">
            <h2>
              {t("parentPortal.actions.title", {
                defaultValue: "Open actions",
              })}
            </h2>
            <p>
              {t("parentPortal.actions.subtitle", {
                defaultValue:
                  "Complete the actions below to keep everything ready for school.",
              })}
            </p>
          </div>

          <span className="pp-actions-help-button" aria-hidden="true">
            <HelpIcon className="" />
          </span>
        </div>

        <div className="pp-actions-summary">
          <SummaryStat
            icon={AlertIcon}
            value={urgentCount}
            label={t("parentPortal.actions.urgent", { defaultValue: "Urgent" })}
            tone="urgent"
          />
          <SummaryStat
            icon={ClockIcon}
            value={waitingCount}
            label={t("parentPortal.actions.waiting_for_you", {
              defaultValue: "Waiting for you",
            })}
            tone="waiting"
          />
          <SummaryStat
            icon={CheckIcon}
            value={processingCount}
            label={t("parentPortal.actions.processing", {
              defaultValue: onlyBankAccountPendingRequests
                ? "Received"
                : "In progress",
            })}
            tone="info"
          />
        </div>

        <p className="pp-actions-total">{totalSubtitle}</p>
      </section>

      {hasOpenCards && (
        <section className="pp-actions-open-list">
          {missingChildren.map((child) => {
            const childName =
              getDisplayName(child) ||
              t("common.unknown_child", { defaultValue: "Child" });
            const classLabel = getClassLabel(child);
            const age = getChildAge(child);

            return (
              <React.Fragment
                key={`missing-${child.id || child.uuid || childName}`}
              >
                <TaskCard
                  tone="urgent"
                  icon={UserIcon}
                  eyebrow={t("parentPortal.actions.urgent", {
                    defaultValue: "Urgent",
                  })}
                  title={t("parentPortal.actions.bsn_missing", {
                    defaultValue: "BSN missing",
                  })}
                  subtitle={childName}
                  details={[
                    [classLabel, age !== null ? `${age} jaar` : ""]
                      .filter(Boolean)
                      .join(" • "),
                  ]}
                  description={t(
                    "parentPortal.actions.bsn_missing_description",
                    {
                      defaultValue:
                        "The BSN is still missing. Add it so the school can complete the administration.",
                    },
                  )}
                  actionLabel={t("parentPortal.actions.fix_bsn", {
                    defaultValue: "Add BSN",
                  })}
                  onClick={() => onFixChild(child)}
                />
              </React.Fragment>
            );
          })}

          {alumniChildren.map((child) => {
            const childName =
              getDisplayName(child) ||
              t("common.unknown_child", { defaultValue: "Child" });

            return (
              <React.Fragment
                key={`alumni-${child.id || child.uuid || childName}`}
              >
                <TaskCard
                  tone="waiting"
                  icon={FileTextIcon}
                  eyebrow={t("parentPortal.actions.waiting_for_you", {
                    defaultValue: "Waiting for you",
                  })}
                  title={t("parentPortal.actions.alumni_title", {
                    defaultValue: "Alumni preferences",
                  })}
                  subtitle={childName}
                  description={t("parentPortal.actions.alumni_missing", {
                    defaultValue: "Preferences are missing",
                  })}
                  actionLabel={t("parentPortal.actions.open", {
                    defaultValue: "Open",
                  })}
                  onClick={() =>
                    openActionDetail({
                      id: `alumni-${child.id || child.uuid || childName}`,
                      tone: "waiting",
                      icon: FileTextIcon,
                      eyebrow: t("parentPortal.actions.waiting_for_you", {
                        defaultValue: "Waiting for you",
                      }),
                      title: t("parentPortal.actions.alumni_title", {
                        defaultValue: "Alumni preferences",
                      }),
                      subtitle: childName,
                      description: t("parentPortal.actions.alumni_missing", {
                        defaultValue: "Preferences are missing",
                      }),
                      actionLabel: t("parentPortal.actions.open", {
                        defaultValue: "Open",
                      }),
                      onAction: () => onAlumniAction(child.id || child.uuid),
                    })
                  }
                />
              </React.Fragment>
            );
          })}

          {privacyActions.map((action) => {
            const child = action?.child;
            const childName =
              action?.childName ||
              getDisplayName(child) ||
              t("common.unknown_child", { defaultValue: "Child" });

            return (
              <React.Fragment
                key={action?.id || `privacy-${child?.id || child?.uuid || childName}`}
              >
                <TaskCard
                  tone="waiting"
                  icon={FileTextIcon}
                  eyebrow={t("parentPortal.actions.waiting_for_you", {
                    defaultValue: "Waiting for you",
                  })}
                  title={t("parentPortal.actions.privacy_title", {
                    defaultValue: "Set privacy preferences",
                  })}
                  subtitle={childName}
                  description={t("parentPortal.actions.privacy_description", {
                    defaultValue:
                      "Choose how photos and information about {{childName}} may be used.",
                    childName,
                  })}
                  actionLabel={t("parentPortal.actions.open", {
                    defaultValue: "Open",
                  })}
                  onClick={() => {
                    setSelectedAction(null);
                    setSelectedRequest(null);
                    onOpenPrivacyAction(child);
                  }}
                />
              </React.Fragment>
            );
          })}

          {healthActions.map((action) => {
            const child = action?.child;
            const childName =
              action?.childName ||
              getDisplayName(child) ||
              t("common.unknown_child", { defaultValue: "Child" });

            return (
              <React.Fragment key={action?.id || `health-${child?.id || child?.uuid || childName}`}>
                <TaskCard
                  tone="waiting"
                  icon={FileTextIcon}
                  eyebrow={t("parentPortal.actions.waiting_for_you", { defaultValue: "Waiting for you" })}
                  title={t("parentPortal.health.action_title", { defaultValue: "Check contacts & notes" })}
                  subtitle={childName}
                  description={t("parentPortal.health.action_description", {
                    defaultValue: "New school year: are the doctor, emergency contact and allergies of {{childName}} still correct?",
                    childName,
                  })}
                  actionLabel={t("parentPortal.actions.open", { defaultValue: "Open" })}
                  onClick={() => {
                    setSelectedAction(null);
                    setSelectedRequest(null);
                    onOpenHealthAction(child);
                  }}
                />
              </React.Fragment>
            );
          })}

          {outingActions.map((request, index) => {
            const childName =
              request.childName ||
              getDisplayName(request.child) ||
              t("common.unknown_child", { defaultValue: "Child" });

            const outingTitle =
              request.outingName ||
              request.label ||
              t("parentPortal.trip_consent.card_title_short", {
                defaultValue: "Trip consent",
              });

            const range = formatRange(request.startsAt, request.expiresAt);

            const dual = !!request.requiresDualConsent;
            const isParent1 =
              currentParentId &&
              request.parent1Id &&
              String(request.parent1Id) === String(currentParentId);
            const isParent2 =
              currentParentId &&
              request.parent2Id &&
              String(request.parent2Id) === String(currentParentId);
            const parent1Done = !!request.parent1ConsentedAt;
            const parent2Done = !!request.parent2ConsentedAt;
            const currentDone =
              (isParent1 && parent1Done) || (isParent2 && parent2Done);
            const otherDone =
              (isParent1 && parent2Done) ||
              (isParent2 && parent1Done) ||
              (!isParent1 && !isParent2 && (parent1Done || parent2Done));

            const meta =
              dual && otherDone && !currentDone
                ? t("parentPortal.trip_consent.approval_needed_short", {
                    defaultValue: "Approval needed",
                  })
                : dual && currentDone && !otherDone
                  ? t("parentPortal.trip_consent.waiting_short", {
                      defaultValue: "Waiting for other parent",
                    })
                  : t("parentPortal.trip_consent.missing_short", {
                      defaultValue: "Consent missing",
                    });

            return (
              <React.Fragment
                key={`outing-${request.id || request.childId || request.targetUrl || index}`}
              >
                <TaskCard
                  tone="waiting"
                  icon={FileTextIcon}
                  eyebrow={t("parentPortal.actions.waiting_for_you", {
                    defaultValue: "Waiting for you",
                  })}
                  title={outingTitle}
                  subtitle={childName}
                  details={[meta, range].filter(Boolean)}
                  actionLabel={t("parentPortal.trip_consent.open_form", {
                    defaultValue: "Open form",
                  })}
                  onClick={() => {
                    onOpenOuting(request);
                    openActionDetail({
                      id: `outing-${request.id || request.childId || index}`,
                      tone: "waiting",
                      icon: FileTextIcon,
                      eyebrow: t("parentPortal.actions.waiting_for_you", {
                        defaultValue: "Waiting for you",
                      }),
                      title: outingTitle,
                      subtitle: childName,
                      details: [meta, range].filter(Boolean),
                      actionLabel: t("parentPortal.trip_consent.open_form", {
                        defaultValue: "Open form",
                      }),
                      onAction: () => onOpenOuting(request),
                      kind: "trip",
                      request,
                    });
                  }}
                />
              </React.Fragment>
            );
          })}

          {openMeetingInvitations.map((invitation) => {
            const title =
              invitation.title ||
              invitation.meetingTitle ||
              t("parentPortal.meetings.title", {
                defaultValue: "Parent meeting",
              });

            const childName =
              invitation.childName ||
              getDisplayName(invitation.child) ||
              invitation.classroomName ||
              "";

            const date =
              formatTimeRange(
                invitation.bookedSlotStart ||
                  invitation.startTime ||
                  invitation.startsAt ||
                  invitation.slotStart ||
                  invitation.dateTime,
                invitation.bookedSlotEnd ||
                  invitation.endTime ||
                  invitation.endsAt ||
                  invitation.slotEnd,
              ) ||
              t("parentPortal.meetings.invitation", {
                defaultValue: "Invitation is ready",
              });

            return (
              <React.Fragment
                key={`meeting-${invitation.id || invitation.uuid || date}`}
              >
                <TaskCard
                  tone="waiting"
                  icon={UsersIcon}
                  eyebrow={t("parentPortal.actions.waiting_for_you", {
                    defaultValue: "Waiting for you",
                  })}
                  title={title}
                  subtitle={childName}
                  details={[date].filter(Boolean)}
                  actionLabel={t("parentPortal.meetings.confirm_appointment", {
                    defaultValue: "Confirm appointment",
                  })}
                  onClick={() =>
                    openActionDetail({
                      id: `meeting-${invitation.id || invitation.uuid || date}`,
                      tone: "waiting",
                      icon: UsersIcon,
                      eyebrow: t("parentPortal.actions.waiting_for_you", {
                        defaultValue: "Waiting for you",
                      }),
                      title,
                      subtitle: childName,
                      details: [date].filter(Boolean),
                      actionLabel: t(
                        "parentPortal.meetings.confirm_appointment",
                        { defaultValue: "Confirm appointment" },
                      ),
                      onAction: () => onOpenParentMeetingInvitation(invitation),
                    })
                  }
                />
              </React.Fragment>
            );
          })}
        </section>
      )}

      {processingCount > 0 && (
        <section className="pp-actions-processing-section">
          <div className="pp-actions-processing-head">
            <h2>
              {t("parentPortal.actions.processing", {
                defaultValue: onlyBankAccountPendingRequests
                  ? "Received changes"
                  : "In progress",
              })}
            </h2>
            <p>
              {onlyBankAccountPendingRequests
                ? t("parentPortal.bank_change_pending", {
                    defaultValue:
                      "Your new bank account number has been saved and is ready for administrative processing.",
                  })
                : t("parentPortal.actions.processing_description", {
                    defaultValue:
                      "These requests have been received and are being processed.",
                  })}
            </p>
          </div>

          <div className="pp-actions-processing-list">
            {pendingRequests.map((request) => (
              <React.Fragment
                key={`request-${request.id || request.uuid || JSON.stringify(request)}`}
              >
                <CompactProcessingItem
                  icon={
                    isBankAccountRequest(request)
                      ? InboxIcon
                      : String(request?.entityType || "").toUpperCase() ===
                          "FAMILY"
                        ? HomeIcon
                        : InboxIcon
                  }
                  title={
                    isBankAccountRequest(request)
                      ? t("parentPortal.actions.bank_change_title", {
                          defaultValue: "Bank account change",
                        })
                      : getMutationRequestChildName(
                          request,
                          childrenList,
                          getRequestSubtitle(request)
                        )
                  }
                  subtitle={
                    isBankAccountRequest(request)
                      ? t("parentPortal.actions.bank_change_action", {
                          defaultValue: "Administrative update",
                        })
                      : getRequestTitle(request)
                  }
                  meta={
                    isBankAccountRequest(request)
                      ? t("parentPortal.bank_change_status", {
                          defaultValue: "Change received",
                        })
                      : t("parentPortal.requests.pending", {
                          defaultValue: "In progress",
                        })
                  }
                  onClick={() => setSelectedRequest(request)}
                />
              </React.Fragment>
            ))}
          </div>
        </section>
      )}

      {totalCount === 0 && (
        <section className="pp-actions-empty">
          <span className="pp-actions-empty-icon" aria-hidden="true">
            <InfoIcon className="h-6 w-6" />
          </span>

          <span>
            <span className="pp-actions-empty-title">
              {t("parentPortal.actions.none", {
                defaultValue: "No open actions",
              })}
            </span>
            <span className="pp-actions-empty-text">
              {t("parentPortal.actions.none_description", {
                defaultValue: "Everything is up to date.",
              })}
            </span>
          </span>
        </section>
      )}
    </div>
  );
};

export default ActionsTab;
